import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import styled, { css } from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import Dropdown from "components/atoms/Dropdown";
import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import { CountTagItem, TagItem, TagText } from "components/atoms/Tag";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Button, { BUTTON_STYLES, ToggleIconButton } from "components/atoms/button";
import Checkbox from "components/atoms/input/Checkbox";
import SelectBox from "components/atoms/input/SelectBox";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import { BodyWithBreadcrumb, Breadcrumb, BreadcrumbTitle } from "components/molecules/Breadcrumb";
import Table from "components/molecules/table/Table";
import TableColumnVisibilityMenu from "components/molecules/table/TableColumnVisibilityMenu";
import {
    compareColumns,
    getColumnOrder,
    getColumnSize,
    getColumnVisibility,
    updateColumnOrder,
} from "components/molecules/table/TableManager";
import ReferenceAddMenu from "components/organisms/reference/ReferenceAddMenu";
import ReferenceTagList from "components/organisms/reference/ReferenceTagList";
import ReferenceAddArxivModal from "components/page/modal/ReferenceAddArxivModal";
import ReferenceAddBibtexModal from "components/page/modal/ReferenceAddBibtexModal";
import ReferenceAddDoiModal from "components/page/modal/ReferenceAddDoiModal";
import ReferenceAddFormModal from "components/page/modal/ReferenceAddFormModal";
import ReferenceAddPubmedModal from "components/page/modal/ReferenceAddPubmedModal";
import ReferenceFileUploadModal from "components/page/modal/ReferenceFileUploadModal";
import NavigatorFolderEditModal from "components/page/modal/NavigatorFolderEditModal";
import NavigatorTagMultiAddModal from "components/page/modal/NavigatorTagMultiAddModal";
import ReferenceSummaryModal from "components/page/modal/ReferenceSummaryModal";
import LoginUserLayout from "components/templates/LoginUserLayout";

import { useReferenceList, useReferenceDelete, useReferenceUpdate } from "hooks/queries/useReference";
import { useNavigatorFolders, useNavigatorTags } from "hooks/queries/useNavigator";
import { useUser } from "hooks/queries/useUser";
import { useSessionPageHistory } from "hooks/useSessionPageHistory";
import { useStableQueryData } from "hooks/useStableQueryData";

import { useDevice } from "utils/device";
import { postReference, postReferenceAddFromSource } from "utils/request/reference";
import { isActiveSpace, isAdminUser, isReadonlyUser } from "utils/user";
import { genericEnterKeyDown } from "utils/validation";

import moment from "moment/moment";

const HeaderLeftWrap = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    gap: 8px;
    min-width: 0;
`;

const HeaderRightWrap = styled.div`
    position: relative;
    flex-shrink: 0;
    margin: 0;
`;

const SelectedFilterWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    overflow-x: auto;
    white-space: nowrap;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const HeaderCountTagItem = styled(CountTagItem)`
    flex-shrink: 0;
    margin: 0px 8px;
`;

const HeaderTitle = styled(BreadcrumbTitle)`
    flex-shrink: 0;
`;

const BodyHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
`;

const BodyHeaderLeftSection = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    width: 171px;
    margin-left: 10px;
`;

const BodyHeaderRightSection = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-end;
    margin: 0 0 0 24px;
`;

const SearchWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 12px 0 0;
    width: 100%;
    min-width: 200px;
    max-width: 320px;
`;

const SearchInputBox = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1px 4px;
    width: 100%;
    height: 28px;
    background-color: var(--color-White);
    border: solid 1px var(--color-Line);
    border-radius: 4px;

    &:focus-within {
        border: solid 1px var(--color-Key);
    }
`;

const SearchInput = styled.input`
    padding: 0 8px;
    width: calc(100% - 24px);
    height: 24px;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-Black);
    background-color: transparent;
    border: transparent;
    outline: var(--color-Key);
`;

const SearchButtonWrap = styled.div`
    display: flex;
    align-items: center;
    height: 24px;
`;

const FavoriteWrap = styled.div`
    display: flex;
    align-items: center;
`;

const DivideLine = styled.span`
    display: block;
    margin: 0 12px;
    width: 1px;
    height: 16px;
    background-color: var(--color-Outline);
`;

const ViewButtonWrap = styled.div`
    display: flex;
    align-items: center;
`;

const BodyContent = styled.div`
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: auto;

    ${(props) =>
        props.$isFileDragging &&
        css`
            border: dashed 2px var(--color-Key);
            background-color: var(--color-Base1);
        `};
`;

const BodyContentUploadView = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const UploadDropBox = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    border: dashed 2px var(--color-Key);
    background-color: var(--color-Base1);
`;

const BodyContentBlankView = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const FileUploadSection = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const AddInfoSection = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 24px;
`;

const AddInfoBox = styled.div`
    flex: 1;
    display: flex;
    justify-content: space-between;
    padding: 16px;
    width: 100%;
    border-radius: 8px;
    background-color: var(--color-InfoBox);

    ${(props) =>
        props.$device.isPC &&
        css`
            padding: 24px;
        `};
`;

const AddInfoItem = styled.div`
    width: 50%;
    max-width: calc(50% - 8px);
`;

const AddInfoItemRow = styled.div`
    display: flex;
    align-items: center;
    margin: 0 0 8px 6px;
    width: 100%;

    &:last-child {
        margin: 0 0 0 6px;
    }
`;

const AddFileBox = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 48px;
    border-radius: 8px;
    border: dashed 2px var(--color-Outline);
    transition: all 150ms ease-in;

    ${(props) =>
        props.$isFileDragging &&
        css`
            border: dashed 2px var(--color-Key);
            background-color: var(--color-Base1);
        `};
`;

const AddBoxIcon = styled.div`
    margin: 0 0 24px 0;
    width: 100px;
    height: 100px;
`;

const HeaderTagItem = styled(TagItem)`
    flex-shrink: 0;
`;

const FolderSeparator = styled.div`
    display: flex;
    align-items: center;
    height: 20px;
`;

const CheckIconStyle = {
    name: "check",
    size: "14",
    color: "var(--color-DarkKey)",
};

const AddInfoItemText = styled(Text).attrs({
    variant: "body-lg-400",
    color: "var(--color-SubBlack)",
    margin: "0 0 0 8px",
})``;

const defaultSearchParams = {
    page: 1,
    page_size: 10,
    ordering: "-created_at",
};
const PAGE_KEY = "referenceList";

const getSearchParamsFromLocationState = (locationState) => {
    if (!locationState) {
        return null;
    }

    const hasFilterState = ["tags", "search", "favorites", "folder"].some((key) => locationState[key] !== undefined);

    if (!hasFilterState) {
        return null;
    }

    const nextParams = {
        ...defaultSearchParams,
    };

    const tags =
        locationState.tags !== undefined ? locationState.tags.filter((tag) => tag !== undefined && tag !== null) : [];
    if (tags.length > 0) {
        nextParams.tags = tags.join(",");
    }

    if (locationState.search) {
        nextParams.search = locationState.search;
    }

    if (locationState.favorites) {
        nextParams.favorites = true;
    }

    if (locationState.folder && locationState.folder.length > 0) {
        nextParams.folder = locationState.folder[0];
    }

    return nextParams;
};

const getRequestSearchParams = (params) => {
    const nextParams = {
        page: params.page,
        page_size: params.page_size,
        ordering: params.ordering,
    };

    if (params.tags) {
        nextParams.tags = params.tags;
    }

    if (params.search) {
        nextParams.search = params.search;
    }

    if (params.favorites) {
        nextParams.favorites = true;
    }

    if (params.folder) {
        nextParams.folder = params.folder;
    }

    return {
        ...nextParams,
    };
};

const EMPTY_REFERENCE_LIST = {
    count: 0,
    results: [],
};

function ReferenceListPage(props) {
    const device = useDevice();
    const nav = useNavigate();
    const { space_uuid } = useParams();
    const navigate = useCallback((path, options) => nav(`/${space_uuid}${path}`, options), [nav, space_uuid]);
    const location = useLocation();
    const { setToast } = useToast();
    const userQuery = useUser();
    const user = useMemo(() => userQuery.data || {}, [userQuery.data]);

    const [searchText, setSearchText] = useState("");
    const [searchParams, setSearchParams] = useState(
        () => getSearchParamsFromLocationState(location.state) ?? { ...defaultSearchParams },
    );
    const referencesQuery = useReferenceList(getRequestSearchParams(searchParams));
    const {
        data: referenceList,
        isLoading: isReferenceListLoading,
        isRefreshing: isReferenceListRefreshing,
    } = useStableQueryData(referencesQuery, EMPTY_REFERENCE_LIST);
    const [tagList, setTagList] = useState([]);
    const tagQuery = useNavigatorTags();
    const [folderList, setFolderList] = useState([]);
    const folderQuery = useNavigatorFolders();

    const queryClient = useQueryClient();
    const tableRef = useRef();
    const rerenderTable = React.useReducer(() => ({}), {})[1];

    // region: SearchParams 처리
    useLayoutEffect(() => {
        // 좌측 사이드 메뉴에서 태그 선택해서 검색하는 경우 처리
        const paramsFromLocationState = getSearchParamsFromLocationState(location.state);
        if (paramsFromLocationState) {
            setSearchParams(paramsFromLocationState);
            return;
        }

        setSearchParams({
            ...defaultSearchParams,
        });
    }, [location.state]);

    const { prevPage } = useSessionPageHistory();

    // searchParams state 가 업데이트 되지 않은 버그 대응
    const searchParamsRef = useRef({});
    useEffect(() => {
        searchParamsRef.current = { ...searchParams };
    }, [searchParams]);

    const saveSessionCache = (params = searchParamsRef.current) => {
        sessionStorage.setItem(
            PAGE_KEY,
            JSON.stringify({
                searchParams: params,
            }),
        );
    };

    const loadSessionCache = () => {
        const sessionCacheJson = sessionStorage.getItem(PAGE_KEY);
        if (sessionCacheJson) {
            const sessionCacheObj = JSON.parse(sessionCacheJson);
            setSearchParams(sessionCacheObj.searchParams);
        }
    };

    const clearSessionCache = () => {
        sessionStorage.removeItem(PAGE_KEY);
    };

    const getCurrentFilterState = useCallback((params = searchParamsRef.current) => {
        return {
            ...(params.tags !== undefined && {
                tags:
                    typeof params.tags === "string"
                        ? params.tags
                              .split(",")
                              .filter((tag) => tag !== "")
                              .map((tag) => Number(tag))
                        : params.tags,
            }),
            ...(params.search !== undefined && { search: params.search }),
            ...(params.favorites !== undefined && { favorites: params.favorites }),
            ...(params.folder !== undefined && { folder: params.folder ? [Number(params.folder)] : [] }),
        };
    }, []);

    const normalizeFilterState = useCallback((state) => {
        const nextState = { ...state };

        if (!nextState.tags || nextState.tags.length === 0) {
            delete nextState.tags;
        }

        if (!nextState.search) {
            delete nextState.search;
        }

        if (!nextState.favorites) {
            delete nextState.favorites;
        }

        if (!nextState.folder || nextState.folder.length === 0) {
            delete nextState.folder;
        }

        return nextState;
    }, []);

    useEffect(() => {
        // 상세 페이지에서 돌아왔을 때 검색창에 텍스트 자동 입력 처리
        setSearchText(searchParams.search || "");
    }, [searchParams.search]);

    useEffect(() => {
        // 참고문헌상세 페이지에서 이동해왔을 때 페이지 복원
        if (prevPage && prevPage.startsWith(`/${space_uuid}/reference/`)) {
            loadSessionCache();
            clearSessionCache(); // 새로고침 했을 때 기본값으로 처리되도록 삭제
        }
    }, [prevPage, space_uuid]);

    useEffect(() => {
        if (tagQuery.data) {
            setTagList(tagQuery.data);
        }
    }, [tagQuery.data]);

    useEffect(() => {
        if (folderQuery.data) {
            setFolderList(folderQuery.data);
        }
    }, [folderQuery.data]);

    // region: Checkbox 선택 동작
    const checkedBibsRef = useRef([]);

    const handleCheckboxAllChange = (event) => {
        // 전체 선택이면 모든 id를, 그렇지 않으면 빈 배열을 반환
        checkedBibsRef.current = event.target.checked ? referenceList?.results.map((item) => item.id) : [];
        rerenderTable();
    };

    const handleCheckboxChangeById = useCallback(
        (id) => {
            const index = checkedBibsRef.current.indexOf(id);
            if (index > -1) {
                checkedBibsRef.current = checkedBibsRef.current.filter((item) => item !== id);
            } else {
                checkedBibsRef.current = [...checkedBibsRef.current, id];
            }
            rerenderTable();
        },
        [rerenderTable],
    );

    const handleCheckboxChange = useCallback(
        (event) => {
            let { id } = event.target;
            id = Number(id);
            handleCheckboxChangeById(id);
        },
        [handleCheckboxChangeById],
    );

    // delete Function
    const deleteReference = () => {
        if (checkedBibsRef.current.length > 0) {
            let citation_exists = false;
            let message = "선택한 참고문헌을 삭제하시겠습니까?";
            referenceList?.results
                .filter((item) => checkedBibsRef.current.includes(item.id))
                .forEach((item) => {
                    if (item.citation_count > 0) {
                        citation_exists = true;
                    }
                });
            if (citation_exists) {
                message = "연구노트에 인용된 참고문헌입니다. 삭제하시겠습니까?";
            }
            Confirm("warn", "참고문헌 삭제", message, "삭제", (e) => {
                handleDeleteConfirm();
            });
        } else {
            Alert("warn", "에러", "삭제할 참고문헌을 선택해주세요.");
        }
    };

    const handleDeleteConfirm = () => {
        deleteReferenceMutation.mutate(
            { ids: checkedBibsRef.current },
            {
                onSuccess: () => {
                    setToast("참고문헌이 삭제되었습니다.", "info");
                    if (referenceList?.results.length === checkedBibsRef.current.length && searchParams.page > 1) {
                        setSearchParams({ ...searchParams, page: searchParams.page - 1 });
                    }
                    queryClient.invalidateQueries("referenceList");
                    checkedBibsRef.current = [];
                },
            },
        );
    };

    const handleExecuteCheckedBibsAction = (checkedBibsAction) => {
        if (isReadonlyUser(user) && !isAdminUser(user)) {
            setToast("읽기 전용 권한으로는 수행할 수 없는 작업입니다.", "warn");
            return;
        }

        if (!isActiveSpace(user.current_space) && isAdminUser(user) && checkedBibsAction !== "delete") {
            setToast("읽기 전용 권한으로는 수행할 수 없는 작업입니다.", "warn");
            return;
        }

        switch (checkedBibsAction) {
            case "delete":
                deleteReference();
                return;
            case "summary": {
                if (checkedBibsRef.current.length > 10) {
                    Alert("warn", "에러", "요약은 10개까지만 가능합니다.");
                    return;
                }
                const indexing = referenceList.results.some((r) => {
                    return checkedBibsRef.current.includes(r.id) && r.file && r.file_status !== "DONE";
                });
                if (indexing) {
                    Alert("warn", "에러", "PDF 인덱싱 중인 참고문헌이 포함되어 있습니다.");
                    return;
                }
                setModalState({
                    ...modalState,
                    summaryModal: !modalState.summaryModal,
                });
                return;
            }
            case "folder":
                setModalState({
                    ...modalState,
                    folderEditModal: !modalState.folderEditModal,
                });
                return;
            case "tag":
                setModalState({
                    ...modalState,
                    tagMultiAddModal: !modalState.tagMultiAddModal,
                });
                return;
            default:
                return;
        }
    };

    // region: 참고문헌 추가, 수정, 삭제
    const [modalState, setModalState] = useState({
        arxivModal: false,
        doiModal: false,
        pubmedModal: false,
        bibtexModal: false,
        tagMultiAddModal: false,
        folderEditModal: false,
        summaryModal: false,
    });

    const [isBibsAddMenuOpen, setIsBibsAddMenuOpen] = useState(false);

    const deleteReferenceMutation = useReferenceDelete();
    const [showFileUploadModal, setShowFileUploadModal] = useState(false);

    const handleOpenAddButton = () => {
        setIsBibsAddMenuOpen((prevState) => !prevState);
    };

    const handleOpenAddBibtexModal = () => {
        setIsBibsAddMenuOpen(false);
        setModalState((prevState) => ({ ...prevState, bibtexModal: true }));
    };

    const handleOpenAddArxivModal = () => {
        setIsBibsAddMenuOpen(false);
        setModalState((prevState) => ({ ...prevState, arxivModal: true }));
    };

    const handleOpenAddDoiModal = () => {
        setIsBibsAddMenuOpen(false);
        setModalState((prevState) => ({ ...prevState, doiModal: true }));
    };

    const handleOpenAddPubmedModal = () => {
        setIsBibsAddMenuOpen(false);
        setModalState((prevState) => ({ ...prevState, pubmedModal: true }));
    };

    const handleOpenAddFormModal = () => {
        setIsBibsAddMenuOpen(false);
        setModalState((prevState) => ({ ...prevState, formModal: true }));
    };

    const referenceMutation = useMutation(
        (variables) => {
            const newBody = {
                ...variables.body,
                ...(searchParams.folder && { folder: searchParams.folder }),
                ...(searchParams.tags && { tags: searchParams.tags.split(",") }),
            };
            if (variables.addFromSource) {
                return postReferenceAddFromSource(space_uuid, { body: newBody });
            }
            return postReference(space_uuid, { body: newBody });
        },
        {
            onSuccess: (data, variables, context) => {
                queryClient.invalidateQueries({ queryKey: "volume" });
                const fileUrl = data.file_url || variables.body.file_url;
                if (fileUrl) {
                    // arxiv 원문 파일 포함하여 추가한 경우
                    navigate(`/reference/${data.reference_no}`, {
                        state: { file: fileUrl },
                    });
                } else {
                    // 파일 없이 추가한 경우 (직접 추가, BibTeX 추가, 외부 DB의 서지정보만 가져와서 추가 등)
                    navigate(`/reference/${data.reference_no}`);
                }
            },
            onError: (error, variables, context) => {
                if (error.response.status === 400) {
                    if (error.response.data.bib_json) {
                        if (error.response.data.bib_json.citation_key) {
                            Alert("warn", "에러", error.response.data.bib_json.citation_key);
                        } else {
                            Alert("warn", "에러", error.response.data.bib_json);
                        }
                    } else if (error.response.data.maximum_references) {
                        Alert("warn", "에러", error.response.data.maximum_references);
                    } else {
                        Alert("warn", "에러", "참고문헌 추가에 실패했습니다.");
                    }
                } else {
                    Alert("warn", "에러", "서버에 오류가 발생했습니다.");
                }
            },
        },
    );

    // drag & drop 파일 업로드
    const [isFileDragging, setIsFileDragging] = useState(false);
    const handleFileDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const types = Array.from(e.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(true);
        }
    };
    const handleFileDragOver = (e) => {
        if (isReadonlyUser(user)) return;
        e.preventDefault();
        e.stopPropagation();
        const types = Array.from(e.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(true);
        }
    };
    const handleFileDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const types = Array.from(e.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(false);
        }
    };
    const handleFileDrop = (e) => {
        if (isReadonlyUser(user)) return;
        e.preventDefault();
        e.stopPropagation();
        const types = Array.from(e.dataTransfer.types);
        if (types.includes("Files")) {
            setIsFileDragging(false);
            if (e.dataTransfer.files) {
                setUploadingFiles(Array.from(e.dataTransfer.files));
                setShowFileUploadModal(true);
            }
        }
    };

    const [uploadingFiles, setUploadingFiles] = useState([]);
    const fileAddHandler = (e) => {
        setUploadingFiles(Array.from(e.target.files));
        setShowFileUploadModal(true);
    };

    //region: Table 컬럼 선언
    const [tableConfig, setTableConfig] = useState(
        localStorage.getItem(PAGE_KEY) ? JSON.parse(localStorage.getItem(PAGE_KEY)).reference : [],
    );
    const updateMutation = useReferenceUpdate();

    const handleBookmarkClick = useCallback(
        (reference) => {
            updateMutation.mutate({
                id: reference.id,
                is_favorite: !reference.is_favorite,
            });
        },
        [updateMutation],
    );

    const handleNavigate = useCallback(
        (state) => {
            // 태그와 폴더 필터가 동시에 남지 않도록 맞춘다.
            const exclusiveState = {
                ...state,
                ...(state.tags !== undefined && { folder: [] }),
                ...(state.folder !== undefined && { tags: [] }),
            };
            const mergedState = normalizeFilterState({
                ...getCurrentFilterState(),
                ...exclusiveState,
            });
            navigate("/reference", { state: mergedState });
        },
        [getCurrentFilterState, navigate, normalizeFilterState],
    );

    const columns = useMemo(
        () => [
            {
                id: "id",
                accessorKey: "id",
                name: "선택",
                header: null,
                size: 36,
                minSize: 36,
                maxSize: 36,
                enableResizing: false,
                content: (info) => {
                    const value = info.getValue();
                    return (
                        <Checkbox
                            id={value}
                            checked={checkedBibsRef.current.includes(value)}
                            onChange={handleCheckboxChange}
                        />
                    );
                },
            },
            {
                id: "is_favorite",
                accessorKey: "is_favorite",
                name: "즐겨찾기",
                size: 36,
                minSize: 36,
                maxSize: 36,
                enableResizing: false,
                defaultVisibility: true,
                header: () => "",
                content: ({ row }) => {
                    return (
                        <Button
                            {...BUTTON_STYLES.iconPlain}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleBookmarkClick(row.original);
                            }}
                            disabled={isReadonlyUser(user)}
                        >
                            {row.original.is_favorite ? (
                                <Icon name={"bookmark"} color={"var(--color-Key)"} size={"16px"} />
                            ) : (
                                <Icon name={"bookmarkDisactive"} size={"16px"} />
                            )}
                        </Button>
                    );
                },
            },
            {
                id: "display_name",
                accessorKey: "display_name",
                name: "display_name",
                size: getColumnSize("display_name", tableConfig) || 500,
                text: (info) => {
                    return info.getValue();
                },
            },
            {
                id: "bib_json.citation_key",
                name: "citation_key",
                defaultVisibility: false,
                size: getColumnSize("bib_json.citation_key", tableConfig),
                text: (info) => {
                    return info.getValue();
                },
                accessorFn: (info) => {
                    return info?.bib_json?.citation_key || "";
                },
            },
            {
                id: "bib_json.title",
                name: "title",
                defaultVisibility: false,
                ...(getColumnSize("bib_json.title", tableConfig) && {
                    size: getColumnSize("bib_json.title", tableConfig),
                }),
                text: (info) => {
                    return info.getValue();
                },
                accessorFn: (info) => {
                    return info?.bib_json?.title || "";
                },
            },
            {
                id: "bib_json.journal",
                name: "journal",
                defaultVisibility: false,
                size: getColumnSize("bib_json.journal", tableConfig) || 150,
                text: (info) => {
                    return info.getValue();
                },
                accessorFn: (info) => {
                    return info?.bib_json?.journal || "";
                },
            },
            {
                id: "bib_json.publisher",
                name: "publisher",
                defaultVisibility: false,
                size: getColumnSize("bib_json.publisher", tableConfig) || 150,
                text: (info) => {
                    return info.getValue();
                },
                accessorFn: (info) => {
                    return info?.bib_json?.publisher || "";
                },
            },
            {
                id: "bib_json.author",
                name: "author",
                size: getColumnSize("bib_json.author", tableConfig) || 100,
                text: (info) => {
                    return info.getValue();
                },
                accessorFn: (info) => {
                    return info?.bib_json?.author || "";
                },
            },
            {
                id: "bib_json.year",
                name: "year",
                size: getColumnSize("bib_json.year", tableConfig) || 50,
                text: (info) => {
                    return info.getValue();
                },
                accessorFn: (info) => {
                    return info?.bib_json?.year || "";
                },
            },
            {
                id: "bib_json.abstract",
                name: "abstract",
                ...(getColumnSize("bib_json.abstract", tableConfig) && {
                    size: getColumnSize("bib_json.abstract", tableConfig),
                }),
                text: (info) => {
                    return info.getValue();
                },
                accessorFn: (info) => {
                    return info?.bib_json?.abstract || "";
                },
                defaultVisibility: false,
            },
            {
                id: "folder",
                accessorKey: "folder",
                name: "folder",
                size: getColumnSize("folder", tableConfig) || 50,
                text: (info) => {
                    const name = folderList.find((folder) => folder.id === Number(info.getValue()))?.name || "";
                    if (name) {
                        return (
                            <Tooltip message={"필터 적용"}>
                                <TagItem
                                    $margin={"0 4px 0 0"}
                                    style={{ maxWidth: "140px" }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleNavigate({
                                            folder: [info.getValue()],
                                        });
                                    }}
                                >
                                    <TagText>{name}</TagText>
                                </TagItem>
                            </Tooltip>
                        );
                    } else {
                        return;
                    }
                },
            },
            {
                id: "tag_mappings",
                accessorKey: "tag_mappings",
                name: "tag",
                size: getColumnSize("tag_mappings", tableConfig) || 50,
                content: (info) => {
                    const tags = info.getValue();
                    if (!tags || tags.length === 0) {
                        return null;
                    }
                    return (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <ReferenceTagList
                                reference={info.row.original}
                                selectedTagIds={
                                    searchParams.tags ? searchParams.tags.split(",").map((tag) => Number(tag)) : []
                                }
                                onNavigate={handleNavigate}
                            />
                        </div>
                    );
                },
            },
            {
                id: "memo",
                accessorKey: "memo",
                name: "memo",
                size: getColumnSize("memo", tableConfig) || 150,
                text: (info) => {
                    return info.getValue();
                },
                defaultVisibility: false,
            },
            {
                id: "created_at",
                accessorKey: "created_at",
                name: "created_at",
                size: getColumnSize("created_at", tableConfig) || 100,
                text: (info) => {
                    return moment(info.getValue()).format("YYYY-MM-DD");
                },
            },
            {
                id: "updated_at",
                accessorKey: "updated_at",
                name: "updated_at",
                size: getColumnSize("updated_at", tableConfig) || 100,
                text: (info) => {
                    return moment(info.getValue()).format("YYYY-MM-DD");
                },
                defaultVisibility: false,
            },
            {
                id: "file",
                accessorKey: "file",
                name: "pdf",
                size: getColumnSize("file", tableConfig) || 50,
                content: (info) => {
                    if (!info.row.original.file) {
                        return (
                            <Tooltip message={"PDF 없음"}>
                                <Button
                                    {...BUTTON_STYLES.iconPlain}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                    <Icon name={"pdf"} color={"var(--color-Grey2)"} size={"18px"} />
                                </Button>
                            </Tooltip>
                        );
                    }
                    return info.row.original.file_status === "DONE" ? (
                        <Tooltip message={"PDF 보기"}>
                            <Button
                                {...BUTTON_STYLES.iconPlain}
                                onClick={(e) => {
                                    if (info.getValue()) {
                                        saveSessionCache(searchParams);
                                        navigate(`/reference/${info.row.original.reference_no}?view=pdf`);
                                    }
                                    e.stopPropagation();
                                }}
                            >
                                <Icon name={"pdf"} color={"var(--color-Key)"} size={"18px"} />
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip message={"PDF 인덱싱 중"}>
                            <AiCursor
                                onClick={() => {
                                    saveSessionCache(searchParams);
                                    navigate(`/reference/${info.row.original.reference_no}`);
                                }}
                            />
                        </Tooltip>
                    );
                },
            },
        ],
        [
            folderList,
            handleBookmarkClick,
            handleCheckboxChange,
            handleNavigate,
            navigate,
            searchParams,
            tableConfig,
            user,
        ],
    );

    useEffect(() => {
        const nextConfig = compareColumns(columns, [...tableConfig]);
        const hasConfigChanged = JSON.stringify(nextConfig) !== JSON.stringify(tableConfig);

        if (hasConfigChanged) {
            setTableConfig(nextConfig);
            return;
        }

        localStorage.setItem(
            PAGE_KEY,
            JSON.stringify({
                reference: tableConfig,
            }),
        );
    }, [columns, tableConfig]);

    // 옵션박스 토글
    const [optionBoxActive, setOptionBoxActive] = useState(false);

    const handleSortChange = (sort) => {
        const sortValue = sort
            .map((sortItem) => {
                return sortItem.desc ? `-${sortItem.id}` : sortItem.id;
            })
            .join(",");
        setSearchParams({ ...searchParams, ordering: sortValue, page: 1 });
    };

    const handlePageChange = (newPage) => {
        setSearchParams({ ...searchParams, page: newPage });
    };

    const handlePageSizeChange = (newSize) => {
        setSearchParams({ ...searchParams, page_size: newSize, page: 1 });
    };

    useEffect(() => {
        checkedBibsRef.current = [];
        rerenderTable();
    }, [referencesQuery.data?.results, rerenderTable]);

    const findParentFolder = (folderId, path = []) => {
        const folder = folderList.find((item) => item.id === folderId);
        if (folder) {
            path.unshift(folder); // 폴더 객체 전체 추가
            if (folder.parent) {
                return findParentFolder(folder.parent, path);
            }
        }
        return path;
    };

    const selectedParamsRenderer = () => {
        if (searchParams.tags && searchParams.tags.length > 0) {
            const tags = searchParams.tags?.split(",").map((tag) => {
                return tagList.find((item) => item.id === Number(tag));
            });

            const tagEls = tags.map((tag, index) => (
                <HeaderTagItem key={index}>
                    <Icon name={"tag"} size={"8"} color={"var(--color-SubBlack)"} />
                    <TagText style={{ margin: "0 8px 0 8px" }}>{tag?.name || ""}</TagText>
                    <Icon
                        name={"x"}
                        size={"8"}
                        color={"var(--color-SubBlack)"}
                        aria-label={`${tag?.name || "태그"} 필터 제거`}
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                            if (!tag) return;
                            handleNavigate({
                                tags: searchParams.tags.split(",").filter((item) => Number(item) !== tag.id),
                            });
                        }}
                    />
                </HeaderTagItem>
            ));

            if (tags.length > 1) {
                tagEls.push(
                    <HeaderTagItem key={tags.length}>
                        <TagText
                            aria-label={"태그 필터 전체 제거"}
                            style={{ cursor: "pointer" }}
                            onClick={(e) => handleNavigate({ tags: [] })}
                        >
                            Clear All
                        </TagText>
                    </HeaderTagItem>,
                );
            }

            return tagEls;
        } else if (searchParams.folder) {
            const folderPath = findParentFolder(Number(searchParams.folder));

            if (folderPath.length === 0) return [];

            return [
                <HeaderTagItem key="all">
                    <TagText
                        aria-label={"폴더 필터 전체 해제"}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleNavigate({ folder: [] })}
                    >
                        모든 참고문헌
                    </TagText>
                </HeaderTagItem>,
                ...folderPath.map((folder, index) => (
                    <React.Fragment key={index}>
                        <FolderSeparator>
                            <Icon name="arrowRight" size="8" color="var(--color-SubBlack)" />
                        </FolderSeparator>
                        <HeaderTagItem>
                            <Icon name="folder" size="8" color="var(--color-SubBlack)" />
                            <TagText
                                style={{ cursor: "pointer", maxWidth: "150px", margin: "0 0 0 8px" }}
                                onClick={() => handleNavigate({ folder: [folder.id] })}
                            >
                                {folder.name}
                            </TagText>
                        </HeaderTagItem>
                    </React.Fragment>
                )),
            ];
        }
    };

    const handleRowDragStart = (e, row) => {
        // 선택된 항목과 현재 행을 추가하여 드래그 데이터 설정
        if (!checkedBibsRef.current.includes(row.original.id)) {
            checkedBibsRef.current = [...checkedBibsRef.current, row.original.id];
            rerenderTable();
        }
        const dragData = {
            resources: checkedBibsRef.current.map((id) => ({
                resource_type: "reference",
                id,
            })),
        };
        e.dataTransfer.setData("application/json", JSON.stringify(dragData));

        // "폴더로 이동" 안내 메시지를 담은 임시 드래그 프리뷰 요소를 생성
        const dragPreview = document.createElement("div");
        dragPreview.style.position = "absolute";
        dragPreview.style.top = "-1000px";
        dragPreview.style.left = "-1000px";
        dragPreview.style.padding = "8px 12px";
        dragPreview.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
        dragPreview.style.color = "#fff";
        dragPreview.style.fontSize = "14px";
        dragPreview.style.borderRadius = "4px";
        dragPreview.innerText = `${checkedBibsRef.current.length}개 요소를 폴더/태그로 이동`;

        // 생성한 요소를 body에 삽입 후 드래그 이미지로 설정
        document.body.appendChild(dragPreview);
        e.dataTransfer.setDragImage(dragPreview, 0, 0);

        // 이미지 설정 직후 임시 요소 제거
        setTimeout(() => {
            document.body.removeChild(dragPreview);
        }, 0);

        // 기본 드래그 효과 설정
        e.dataTransfer.effectAllowed = "move";
    };

    //region: 페이지 레이아웃 마크업
    return (
        <LoginUserLayout searchParams={searchParams}>
            <HtmlHead title={"참고문헌"} />

            <Breadcrumb>
                <HeaderLeftWrap>
                    <HeaderTitle>참고문헌</HeaderTitle>
                    <SelectedFilterWrap>{selectedParamsRenderer()}</SelectedFilterWrap>
                    <HeaderCountTagItem>
                        <TagText>{referenceList.count}건</TagText>
                    </HeaderCountTagItem>
                </HeaderLeftWrap>
                <HeaderRightWrap>
                    <Dropdown
                        open={isBibsAddMenuOpen}
                        onOpenChange={setIsBibsAddMenuOpen}
                        placement="bottom-center"
                        offset={4}
                        anchor={
                            <Button
                                onClick={handleOpenAddButton}
                                {...BUTTON_STYLES.actionPrimary}
                                width={"132px"}
                                height={"32px"}
                                bgColor={isBibsAddMenuOpen ? "var(--color-White)" : "var(--color-Button2)"}
                                hoverBgColor={isBibsAddMenuOpen ? "transparent" : "var(--color-ButtonHover2)"}
                                border={isBibsAddMenuOpen ? "solid 2px var(--color-Button2)" : "solid 2px transparent"}
                                fontColor={isBibsAddMenuOpen ? "var(--color-Black)" : "var(--color-White)"}
                                buttonText={"참고문헌 추가"}
                                disabled={isReadonlyUser(user)}
                            >
                                <Icon
                                    name={"plus"}
                                    size={"12"}
                                    color={isBibsAddMenuOpen ? "var(--color-Black)" : "var(--color-White)"}
                                />
                            </Button>
                        }
                    >
                        <ReferenceAddMenu
                            fileAddHandler={fileAddHandler}
                            onOpenBibtex={handleOpenAddBibtexModal}
                            onOpenArxiv={handleOpenAddArxivModal}
                            onOpenDoi={handleOpenAddDoiModal}
                            onOpenPubmed={handleOpenAddPubmedModal}
                            onOpenForm={handleOpenAddFormModal}
                        />
                    </Dropdown>
                </HeaderRightWrap>
            </Breadcrumb>

            <BodyWithBreadcrumb>
                {(referenceList?.count > 0 ||
                    searchParams.tags?.length > 0 ||
                    searchParams.search?.length > 0 ||
                    searchParams.folder ||
                    searchParams.favorites) && (
                    <BodyHeader>
                        <BodyHeaderLeftSection>
                            <Checkbox
                                margin={"0 20px 0 0"}
                                indeterminate={
                                    checkedBibsRef.current.length > 0 &&
                                    checkedBibsRef.current.length < referenceList?.results?.length
                                }
                                checked={
                                    referenceList?.results?.length > 0 &&
                                    checkedBibsRef.current.length === referenceList?.results?.length
                                }
                                onChange={handleCheckboxAllChange}
                            />
                            <SelectBox
                                width={"auto"}
                                height={"28px"}
                                optionWidth={"280px"}
                                optionAlign={"left"}
                                optionMaxHeight={"auto"}
                                items={[
                                    { id: "", name: "--- 액션을 선택하세요. ---" },
                                    { id: "delete", name: "선택된 항목을 삭제합니다." },
                                    { id: "summary", name: "선택된 항목을 요약합니다." },
                                    { id: "folder", name: "선택된 항목을 다른 폴더로 이동합니다." },
                                    { id: "tag", name: "선택된 항목에 다른 태그를 추가합니다." },
                                ]}
                                textAlign={"left"}
                                value={""}
                                onChange={(value) => {
                                    if (checkedBibsRef.current.length === 0) {
                                        setToast("선택된 항목이 없습니다.", "warn");
                                        return;
                                    }
                                    handleExecuteCheckedBibsAction(value);
                                }}
                                disabled={isReadonlyUser(user) && !isAdminUser(user)}
                            />
                            {checkedBibsRef.current.length > 0 && (
                                <Text margin="0 0 0 10px" variant="body-sm-400" fontWeight="300">
                                    {checkedBibsRef.current.length}개의 항목이 선택됨
                                </Text>
                            )}
                        </BodyHeaderLeftSection>
                        <BodyHeaderRightSection>
                            <SearchWrap>
                                <SearchInputBox>
                                    <SearchInput
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        onKeyDown={(e) => {
                                            genericEnterKeyDown(e, () => {
                                                setSearchParams({ ...searchParams, search: searchText, page: 1 });
                                            });
                                        }}
                                    />
                                    <SearchButtonWrap>
                                        {searchText && (
                                            <Tooltip message={"초기화"}>
                                                <Button
                                                    {...BUTTON_STYLES.iconSurface}
                                                    onClick={(e) => {
                                                        setSearchText("");
                                                        setSearchParams({ ...searchParams, search: "", page: 1 });
                                                    }}
                                                >
                                                    <Icon name={"x"} color={"var(--color-SubBlack)"} />
                                                </Button>
                                            </Tooltip>
                                        )}
                                        <Tooltip message={"검색"}>
                                            <Button
                                                {...BUTTON_STYLES.iconSurface}
                                                onClick={(e) => {
                                                    setSearchParams({ ...searchParams, search: searchText, page: 1 });
                                                }}
                                            >
                                                <Icon name={"search"} size={"12"} color={"var(--color-SubBlack)"} />
                                            </Button>
                                        </Tooltip>
                                    </SearchButtonWrap>
                                </SearchInputBox>
                            </SearchWrap>
                            <FavoriteWrap>
                                <Tooltip message={"즐겨찾기만 보기"}>
                                    <Button
                                        {...BUTTON_STYLES.iconSurfaceStatic}
                                        aria-label={"즐겨찾기만 보기"}
                                        buttonSize={"24px"}
                                        border={
                                            searchParams.favorites
                                                ? "solid 2px var(--color-Key)"
                                                : "solid 2px var(--color-DisabledButton)"
                                        }
                                        onClick={() => {
                                            handleNavigate({
                                                favorites: !searchParams.favorites,
                                            });
                                        }}
                                    >
                                        <Icon
                                            name={"bookmark"}
                                            size={"12px"}
                                            color={
                                                searchParams.favorites
                                                    ? "var(--color-Key)"
                                                    : "var(--color-DisabledButton)"
                                            }
                                        />
                                    </Button>
                                </Tooltip>
                            </FavoriteWrap>
                            <DivideLine />
                            <Tooltip message={"테이블에 표시될 항목을 선택합니다."}>
                                <ViewButtonWrap>
                                    <Dropdown
                                        open={optionBoxActive}
                                        onOpenChange={setOptionBoxActive}
                                        placement="bottom-end"
                                        offset={4}
                                        anchor={
                                            <ToggleIconButton
                                                active={optionBoxActive}
                                                variant="secondary"
                                                margin="0 4px 0 0"
                                                buttonSize="24px"
                                                renderIcon={(active) => (
                                                    <Icon
                                                        name={"option"}
                                                        color={active ? "var(--color-DarkKey)" : "var(--color-Black)"}
                                                        size={"14px"}
                                                    />
                                                )}
                                                onClick={() => {
                                                    setOptionBoxActive((prev) => !prev);
                                                }}
                                            />
                                        }
                                    >
                                        <TableColumnVisibilityMenu
                                            columns={columns}
                                            tableConfig={tableConfig}
                                            setTableConfig={setTableConfig}
                                            hiddenColumnIds={["id", "is_favorite", "display_name"]}
                                        />
                                    </Dropdown>
                                </ViewButtonWrap>
                            </Tooltip>
                        </BodyHeaderRightSection>
                    </BodyHeader>
                )}
                {isReferenceListLoading ? null : referenceList.count > 0 ? (
                    <BodyContent
                        onDragEnter={handleFileDragEnter}
                        onDragOver={handleFileDragOver}
                        onDragLeave={handleFileDragLeave}
                        onDrop={handleFileDrop}
                        $isFileDragging={isFileDragging}
                        disabled={isReadonlyUser(user)}
                    >
                        {isFileDragging ? (
                            <BodyContentUploadView>
                                <UploadDropBox>
                                    <Icon name={"fileUploadOn"} size={"100px"} />
                                    <Text margin="24px 0 0 0" variant="body-lg-400" textAlign="center">
                                        현재 목록에 <strong>참고문헌 파일</strong>을 업로드합니다.
                                    </Text>
                                </UploadDropBox>
                            </BodyContentUploadView>
                        ) : (
                            <Table
                                ref={tableRef}
                                tableKey={"reference"}
                                data={referenceList?.results || []}
                                columns={columns}
                                useSizingColumns={true}
                                onSizeChange={(changedColumn) => {
                                    Object.keys(changedColumn).forEach((key) => {
                                        setTableConfig((prev) =>
                                            prev.map((item) =>
                                                item.id === key ? { ...item, size: changedColumn[key] } : item,
                                            ),
                                        );
                                    });
                                }}
                                useSortingColumns={true}
                                useOrderColumns={true}
                                columnVisibility={getColumnVisibility(tableConfig)}
                                pagination={{
                                    pageIndex: searchParams.page,
                                    pageSize: searchParams.page_size,
                                    count: referenceList?.count,
                                }}
                                setPagination={(page) => {
                                    if (Number(searchParams.page_size) !== Number(page.pageSize)) {
                                        handlePageSizeChange(page.pageSize);
                                    } else if (searchParams.page !== Number(page.pageIndex)) {
                                        handlePageChange(page.pageIndex);
                                    } else {
                                        return;
                                    }
                                }}
                                sorting={
                                    searchParams?.ordering
                                        ? [
                                              {
                                                  id: searchParams.ordering.replace("-", ""),
                                                  desc: searchParams.ordering.startsWith("-"),
                                              },
                                          ]
                                        : []
                                }
                                setSorting={handleSortChange}
                                onRowClick={(row) => {
                                    saveSessionCache(searchParams);
                                    navigate(`/reference/${row.original.reference_no}`);
                                }}
                                onRowDragStart={handleRowDragStart}
                                columnOrder={getColumnOrder(tableConfig)}
                                setColumnOrder={(oldIndex, newIndex) =>
                                    setTableConfig(updateColumnOrder(tableConfig, oldIndex, newIndex))
                                }
                                checkedRowIds={checkedBibsRef.current}
                                isRefreshing={isReferenceListRefreshing}
                            />
                        )}
                    </BodyContent>
                ) : (
                    <>
                        {searchParams.search?.length > 0 || searchParams.favorites ? (
                            <BodyContentBlankView>
                                <Text variant="body-lg-400" margin="0 0 16px 0">
                                    일치하는 참고문헌이 없습니다.
                                </Text>
                            </BodyContentBlankView>
                        ) : isReadonlyUser(user) ? (
                            <BodyContentBlankView>
                                <Text variant="body-lg-400" margin="0 0 16px 0">
                                    참고문헌이 없습니다.
                                </Text>
                            </BodyContentBlankView>
                        ) : (
                            <BodyContentBlankView
                                onDragEnter={handleFileDragEnter}
                                onDragOver={handleFileDragOver}
                                onDragLeave={handleFileDragLeave}
                                onDrop={handleFileDrop}
                            >
                                <FileUploadSection>
                                    <AddFileBox $isFileDragging={isFileDragging}>
                                        <Text variant="body-lg-400" margin="0 0 24px 0">
                                            참고문헌이 없습니다.
                                        </Text>
                                        <AddBoxIcon>
                                            <Icon
                                                name={isFileDragging ? "fileUploadOn" : "fileUploadOff"}
                                                size={"100px"}
                                            />
                                        </AddBoxIcon>
                                        <Text margin="0 0 4px 0" variant="body-lg-400" textAlign="center">
                                            <strong>[참고문헌 추가]</strong> 버튼을 누르거나 PDF 파일을 이곳에 드래그해
                                            추가하세요.
                                        </Text>
                                        <Text variant="body-lg-400" textAlign="center">
                                            참고문헌에는 현재 선택된 폴더 또는 태그가 자동 적용됩니다.
                                        </Text>
                                    </AddFileBox>
                                </FileUploadSection>
                                <AddInfoSection>
                                    <AddInfoBox $device={device}>
                                        <AddInfoItem>
                                            <Text margin="0 0 16px 0" variant="body-lg-400" fontWeight="600">
                                                참고문헌을 추가하면 다음 기능을 이용할 수 있습니다.
                                            </Text>
                                            <AddInfoItemRow>
                                                <Icon {...CheckIconStyle} />
                                                <AddInfoItemText>웹 저장소로 어디서나 접근</AddInfoItemText>
                                            </AddInfoItemRow>
                                            <AddInfoItemRow>
                                                <Icon {...CheckIconStyle} />
                                                <AddInfoItemText>연관 논문 추천</AddInfoItemText>
                                            </AddInfoItemRow>
                                            <AddInfoItemRow>
                                                <Icon {...CheckIconStyle} />
                                                <AddInfoItemText>연구노트에 연결 및 인용문 스타일 전환</AddInfoItemText>
                                            </AddInfoItemRow>
                                            <AddInfoItemRow>
                                                <Icon {...CheckIconStyle} />
                                                <AddInfoItemText>BibTeX 다운로드</AddInfoItemText>
                                            </AddInfoItemRow>
                                        </AddInfoItem>
                                        <AddInfoItem>
                                            <Text margin="0 0 16px 0" variant="body-lg-400" fontWeight="600">
                                                PDF 파일을 추가하면 더 많은 기능들을 사용할 수 있습니다.
                                            </Text>
                                            <AddInfoItemRow>
                                                <Icon {...CheckIconStyle} />
                                                <AddInfoItemText>서지정보 및 태그 자동 추출</AddInfoItemText>
                                            </AddInfoItemRow>
                                            <AddInfoItemRow>
                                                <Icon {...CheckIconStyle} />
                                                <AddInfoItemText>
                                                    PDF 리뷰 환경 (하이라이트 메모, AI 리뷰 기능)
                                                </AddInfoItemText>
                                            </AddInfoItemRow>
                                            <AddInfoItemRow>
                                                <Icon {...CheckIconStyle} />
                                                <AddInfoItemText>
                                                    PDF 자동 요약 (연구 주제·방법·결과 요약)
                                                </AddInfoItemText>
                                            </AddInfoItemRow>
                                            <AddInfoItemRow>
                                                <Icon {...CheckIconStyle} />
                                                <AddInfoItemText>논문에 대한 AI 비평문 초안 작성</AddInfoItemText>
                                            </AddInfoItemRow>
                                        </AddInfoItem>
                                    </AddInfoBox>
                                </AddInfoSection>
                            </BodyContentBlankView>
                        )}
                    </>
                )}
            </BodyWithBreadcrumb>
            {modalState.arxivModal && (
                <ReferenceAddArxivModal
                    exitModal={(e) =>
                        setModalState({
                            ...modalState,
                            arxivModal: !modalState.arxivModal,
                        })
                    }
                    mutation={referenceMutation}
                />
            )}
            {modalState.doiModal && (
                <ReferenceAddDoiModal
                    exitModal={(e) => setModalState({ ...modalState, doiModal: !modalState.doiModal })}
                    mutation={referenceMutation}
                />
            )}
            {modalState.pubmedModal && (
                <ReferenceAddPubmedModal
                    exitModal={(e) =>
                        setModalState({
                            ...modalState,
                            pubmedModal: !modalState.pubmedModal,
                        })
                    }
                    mutation={referenceMutation}
                />
            )}
            {modalState.bibtexModal && (
                <ReferenceAddBibtexModal
                    exitModal={(e) =>
                        setModalState({
                            ...modalState,
                            bibtexModal: !modalState.bibtexModal,
                        })
                    }
                    searchParams={searchParams}
                />
            )}
            {modalState.formModal && (
                <ReferenceAddFormModal
                    exitModal={(e) => setModalState({ ...modalState, formModal: !modalState.formModal })}
                    mutation={referenceMutation}
                />
            )}
            {showFileUploadModal && (
                <ReferenceFileUploadModal
                    uploadingFiles={uploadingFiles}
                    exitModal={(e) => {
                        setShowFileUploadModal(false);
                        referencesQuery.refetch();
                    }}
                    searchParams={searchParams}
                />
            )}
            {modalState.tagMultiAddModal && (
                <NavigatorTagMultiAddModal
                    exitModal={() =>
                        setModalState({
                            ...modalState,
                            tagMultiAddModal: !modalState.tagMultiAddModal,
                        })
                    }
                    resources={checkedBibsRef.current.map((id) => ({ resource_type: "reference", id }))}
                />
            )}
            {modalState.folderEditModal && (
                <NavigatorFolderEditModal
                    exitModal={() =>
                        setModalState({
                            ...modalState,
                            folderEditModal: !modalState.folderEditModal,
                        })
                    }
                    resources={checkedBibsRef.current.map((id) => ({ resource_type: "reference", id }))}
                />
            )}
            {modalState.summaryModal && (
                <ReferenceSummaryModal
                    papers={{
                        type: "referenceList",
                        list: checkedBibsRef.current.map((id) => referenceList.results.find((item) => item.id === id)),
                    }}
                    exitModal={(e) => setModalState({ ...modalState, summaryModal: !modalState.summaryModal })}
                />
            )}
        </LoginUserLayout>
    );
}

export default ReferenceListPage;
